import { useEffect, useState } from "react";
import { FaExclamationCircle, FaFilter } from "react-icons/fa";
import { FaCheck } from "react-icons/fa6";
import { useAppContext } from "../../context/AppContext.tsx";
import TooltipButton from "./TooltipButton.tsx";

interface request {
    searched: (vals: any[]) => void,
    needLabel: boolean,
    assignedRoles?: any[],
    needWarningAndCount: boolean
}

export function DimensionFilter({ searched, needLabel, assignedRoles = [], needWarningAndCount = false }: request) {
    const [selectedDimensions, setSelectedDimensions] = useState<any[]>([]);
    const { dimensions } = useAppContext();

    useEffect(() => {
        searched(selectedDimensions);
    }, [selectedDimensions])

    return (
        <div className="d-flex flex-wrap gap-2 align-items-center py-2">
            {
                needLabel &&
                <div className="d-flex flex-row align-items-center gap-1 text-muted fw-semibold" style={{ fontSize: '0.9rem' }}>
                    <FaFilter size={12} />
                    Dimensions:
                </div>
            }

            {dimensions.map((dim) => {
                const selected = selectedDimensions.includes(dim.dimensionCode);
                const assignedCount = assignedRoles.filter(r => r.dataMapCode === dim.dimensionCode).length;
                const isMissing = (needWarningAndCount) ? assignedCount === 0 : false;

                return (
                    <TooltipButton
                        key={dim.dimensionCode}
                        title={
                            needWarningAndCount ? (
                                isMissing
                                    ? `Missing role for ${dim.dimensionName}. Click to filter available roles.`
                                    : `${assignedCount} role(s) assigned. Click to ${selected ? "remove filter" : "filter by this dimension"}.`) :
                                `Click to ${selected ? "remove filter" : "filter by this dimension"}`
                        }
                    >
                        <button
                            onClick={() =>
                                setSelectedDimensions(prev =>
                                    selected ? prev.filter(d => d !== dim.dimensionCode) : [...prev, dim.dimensionCode]
                                )
                            }
                            className="btn btn-sm rounded-pill d-flex align-items-center gap-2"
                            style={{
                                // ALWAYS keep the family background color
                                backgroundColor: dim.bg,

                                // ALWAYS keep the family text color to easily identify the group
                                color: dim.color,

                                // Error: Dashed Red Border. Normal: Solid Family Color (if selected) or faint border.
                                border: isMissing
                                    ? `1.5px dashed #dc3545`
                                    : `1.5px solid ${selected ? dim.color : 'rgba(0,0,0,0.05)'}`,

                                fontWeight: selected ? '600' : '500',
                                transition: "all 0.2s ease-in-out",
                                padding: '0.35rem 0.8rem',
                                boxShadow: selected && isMissing ? '0 0 0 0.2rem rgba(220, 53, 69, 0.15)' : 'none'
                            }}
                        >
                            {selected && <FaCheck size={12} className="flex-shrink-0" />}
                            <span className="text-truncate">{dim.dimensionName}</span>
                            {
                                needWarningAndCount &&
                                <>
                                    {isMissing ? (
                                        <FaExclamationCircle className="text-danger flex-shrink-0" size={14} />
                                    ) : (
                                        <span
                                            className="badge rounded-pill d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm"
                                            style={{
                                                backgroundColor: selected ? dim.color : '#6c757d',
                                                color: '#fff',
                                                fontSize: '0.7rem',
                                                fontWeight: '600',
                                                minWidth: '22px', // Forces a perfect circle for single digits
                                                height: '22px',   // Fixed height to ensure perfect vertical centering
                                                padding: '0 6px', // Horizontal padding to expand gracefully for long numbers
                                                lineHeight: '1',
                                                transition: "background-color 0.2s"
                                            }}
                                        >
                                            {assignedCount > 99 ? '99+' : assignedCount}
                                        </span>
                                    )}
                                </>
                            }
                        </button>
                    </TooltipButton>
                );
            })}
        </div>
    )
}

export default DimensionFilter;